import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { newsLetterSubscribe } from '@medifind/interface';
import { Button, Input, MFLink } from '@medifind/shared-basic-components';
import formsStyles from '@medifind/styles/components/form.module.scss';
import { Yup, addYupValidationsForPM } from '@medifind/utils';
import { logEventGA4 } from '@medifind/zustand';
import styles from './NewsletterForm.module.scss';
addYupValidationsForPM(Yup);

const successMessageTime = 60000;

const ValidationError = ({ field }) =>
  field && (
    <div
      className={classNames(formsStyles['feedback'], formsStyles['feedback--invalid'], styles['invalid-feedback'])}
      title={field?.message}
    >
      {field?.message}
    </div>
  );

const NewsletterForm = ({ location, title = 'Subscribe to our newsletter', askForName = false, askForGivenName = true, classes = {} }) => {
  const validationSchema = Yup.object().shape({
    newsletterEmail: Yup.string().label('Email').email('Valid email required').nullable().required('Required'),
    ...(askForName
      ? {
          givenName: Yup.string()
            .label('Given Name')
            .nullable()
            .required('Required')
            .hasValidName()
            .stringMinLetterAmount('Given Name must have at least 2 letters', 2),
          familyName: Yup.string()
            .label('Family Name')
            .nullable()
            .required('Required')
            .hasValidName()
            .stringMinLetterAmount('Family Name must have at least 2 letters', 2),
        }
      : askForGivenName
      ? {
          givenName: Yup.string()
            .label('Full Name')
            .nullable()
            .required('Required')
            .hasValidName('Full Name is not a valid name')
            .stringMinLetterAmount('Full Name must have at least 2 letters', 2),
        }
      : {}),
  });

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      newsletterEmail: '',
      ...(askForName ? { givenName: '', familyName: '' } : askForGivenName ? { givenName: '' } : {}),
    },
    resolver: yupResolver(validationSchema),
  });
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    reset,
    register,
  } = methods;

  const [wasSuccess, setWasSuccess] = useState(false);

  const onSubmit = (values) => {
    return newsLetterSubscribe({
      email: values.newsletterEmail,
      givenName: values.givenName,
      familyName: values.familyName,
    })
      .then((data) => {
        logEventGA4('newsletter', { location });
        setWasSuccess(true);
        reset();
        setTimeout(() => {
          setWasSuccess(false);
        }, successMessageTime);
      })
      .catch(({ message }) => {
        logEventGA4('exception', { type: 'newsletter', description: 'Newsletter Form Submit Error', fatal: false });
        if (message) {
          setError('general', { message });
        }
      });
  };

  return (
    <div className={classNames(styles['newsletter'], classes.newsletter)}>
      <h5 className={classNames(styles['title'], classes.title)}>{title}</h5>
      <FormProvider {...methods} enableReinitialize>
        {errors?.general && <Alert variant="warning">{errors?.general?.message}</Alert>}
        {wasSuccess ? (
          <Alert variant="success">Thank you for subscribing!</Alert>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className={formsStyles['form']}>
            <p className={classNames(styles['instructions'], classes.instructions)}>
              Sign up to stay informed about MediFind and get wellness sent your way.
            </p>
            <div className={classNames(classes.formFields)}>
              <div>
                {(askForName || askForGivenName) && (
                  <>
                    <label className={classNames(styles['input-label'], classes.inputLabel)} htmlFor="full-name">
                      Full Name
                    </label>
                    <div className={classNames(styles['input-group'], classes.inputGroup)} style={{ maxWidth: 300 }}>
                      <Input
                        {...register('givenName')}
                        className={classNames(styles['input'], { [styles['form-input-error']]: Boolean(errors?.givenName) })}
                        type="text"
                        placeholder="Full Name"
                        label="Full Name"
                        autoComplete="given-name"
                        id="full-name"
                      />
                    </div>
                    <ValidationError field={errors?.givenName} />
                  </>
                )}
              </div>
              {askForName && (
                <div>
                  <label className={classNames(styles['input-label'], classes.inputLabel)} htmlFor="last-name">
                    Last Name
                  </label>
                  <div className={classNames(styles['input-group'], classes.inputGroup)} style={{ maxWidth: 300 }}>
                    <Input
                      {...register('familyName')}
                      className={classNames(styles['input'], { [styles['form-input-error']]: Boolean(errors?.familyName) })}
                      type="text"
                      placeholder="Last Name"
                      label="Last Name"
                      autoComplete="family-name"
                    />
                  </div>
                  <ValidationError field={errors?.familyName} />
                </div>
              )}
              <div>
                <label className={classNames(styles['input-label'], classes.inputLabel)} htmlFor="email">
                  Email
                </label>
                <div className={classNames(styles['input-group'], classes.inputGroup)} style={{ maxWidth: 300 }}>
                  <Input
                    {...register('newsletterEmail')}
                    className={classNames(styles['input'], { [styles['form-input-error']]: Boolean(errors?.newsletterEmail) })}
                    type="email"
                    placeholder="Email"
                    autoComplete="email"
                    label="Email"
                    id="email"
                  />
                </div>
                <ValidationError field={errors?.newsletterEmail} />
              </div>
              <Button
                classes={{ content: styles['sign-me-up-button'] }}
                label="Sign me up"
                onClick={handleSubmit(onSubmit)}
                type="normal"
                color="blue"
              />
            </div>
          </form>
        )}
      </FormProvider>
      <p className={classNames(styles['disclaimer'], classes.disclaimer)}>
        By subscribing, I agree to MediFind's{' '}
        <MFLink to="/terms" target="_blank">
          Terms of Use
        </MFLink>
        {', '}
        <MFLink to="/privacy" target="_blank">
          Privacy Policy
        </MFLink>{' '}
        and{' '}
        <MFLink to="/health-data-privacy" target="_blank">
          Consumer Health Data Privacy Policy
        </MFLink>
        , as applicable.
      </p>
    </div>
  );
};

NewsletterForm.propTypes = {
  location: PropTypes.string.isRequired,
  title: PropTypes.string,
  askForName: PropTypes.bool,
  askForGivenName: PropTypes.bool,
  classes: PropTypes.object,
};

export { NewsletterForm as default, NewsletterForm };
